import { Box, CircularProgress, Typography } from '@mui/material';
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined';
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { updateObjectStateValue } from "../../../utils/FunctionHelpers";
import { withSnackbar } from "../../../utils/SnackbarHOC";
import { importCandidatsToSelection } from "../../../services/SelectionCandidatService";
import Modal from "../../../components/Modal/Modal";
import { AppButton } from "../../../components/UI";
import { ImportFileStep, ImportStepper, ImportSummary, parseXlsx } from "../ImportWizardParts";
import Step1 from "./Components/Step1";
import Step3 from "./Components/Step3";

const steps = [
  'Choisir une sélection',
  'Importer le fichier',
  'Vérifier et importer',
];

const REQUIRED_HEADERS = ["firstName", "lastName", "occupation", "linkedinEmail", "phoneNumbers", "linkedinUrl", "company_name"];

// LinkedIn exports prefix phone numbers with their type ("MOBILE: +33 …").
const cleanRow = (row) => (row.phoneNumbers !== undefined
  ? { ...row, phoneNumbers: String(row.phoneNumbers).replace(/HOME: |MOBILE: |WORK: | /gi, '') }
  : row);

const CandidatImport = ({ snackbarShowMessage, setModalState, modalState }) => {
  const [importData, setImportData] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState({});
  const history = useHistory();

  const resetStatesData = () => {
    setImportData({});
    setErrors({});
    setCurrentStep(0);
  };

  const handleClose = () => {
    if (importData.isLoading) return;
    setModalState({ open: false });
    resetStatesData();
  };

  const handleSubmit = () => {
    snackbarShowMessage("Importation des candidats...", "warning");
    updateObjectStateValue(setImportData, { isLoading: true });
    const payload = {
      selection: importData.selection.id,
      candidatsToImport: importData.candidatsToImport,
    };
    importCandidatsToSelection(payload).then(() => {
      snackbarShowMessage("Candidat a été ajouté dans les sélections.");
      setModalState({ open: false });
      resetStatesData();
      history.push({
        pathname: `/selections/${importData.selection.id}/candidats/list`,
        search: `?selectionName=${importData.selection.selectionName}&consultant=${importData.selection.creator}`
      });
    }).catch(({ response }) => {
      snackbarShowMessage(response?.data?.error?.detail || "Une erreur est survenue.", "error", 4000);
    }).finally(() => {
      updateObjectStateValue(setImportData, { isLoading: false });
    });
  };

  const validate = async () => {
    const temp = {};
    if (currentStep === 0) {
      temp.selection = importData.selection ? "" : "Choisissez la sélection dans laquelle importer les candidats.";
    }
    if (currentStep === 1) {
      if (!importData.candidatsFile) {
        temp.candidatsFile = "Ajoutez le fichier Excel des candidats.";
      } else {
        updateObjectStateValue(setImportData, { isImporting: true });
        try {
          const { rows, missing } = await parseXlsx(importData.candidatsFile, REQUIRED_HEADERS, cleanRow);
          updateObjectStateValue(setImportData, { candidatsToImport: rows });
          temp.candidatsFile = missing.length
            ? `Colonne${missing.length > 1 ? 's' : ''} manquante${missing.length > 1 ? 's' : ''} dans le fichier : ${missing.join(', ')}.`
            : rows.length === 0 ? "Le fichier ne contient aucune ligne de candidat." : "";
        } catch {
          temp.candidatsFile = "Impossible de lire ce fichier. Vérifiez qu'il s'agit bien d'un fichier Excel (.xlsx).";
        } finally {
          updateObjectStateValue(setImportData, { isImporting: false });
        }
      }
    }
    setErrors(temp);
    return Object.values(temp).every((x) => x === "");
  };

  const handleNextStep = async () => {
    if (await validate()) setCurrentStep((s) => s + 1);
  };

  const rowsCount = importData.candidatsToImport?.length || 0;

  const getStepContent = () => {
    switch (currentStep) {
      case 0:
        return <Step1 {...{ importData, setImportData, errors }} />;
      case 1:
        return (
          <ImportFileStep
            file={importData.candidatsFile}
            onFile={(file, error) => { updateObjectStateValue(setImportData, { candidatsFile: file || undefined }); setErrors({ candidatsFile: error || "" }); }}
            requiredHeaders={REQUIRED_HEADERS}
            templateName="modele_import_candidats.xlsx"
            entityLabel="candidats"
            error={errors.candidatsFile}
          />
        );
      case 2:
        return (
          <>
            <ImportSummary
              count={rowsCount} entityLabel="candidats" targetLabel="la sélection" targetName={importData.selection?.selectionName}
              emptyEmails={(importData.candidatsToImport || []).filter((c) => !String(c.linkedinEmail || '').trim()).length}
            />
            <Step3 {...{ importData, setImportData }} />
          </>
        );
      default:
        return "Etape inconnue.";
    }
  };

  const actions = (
    <>
      <AppButton variant="secondary" onClick={currentStep > 0 ? () => setCurrentStep((s) => s - 1) : handleClose} disabled={importData.isLoading || importData.isImporting}>
        {currentStep > 0 ? "Retour" : "Annuler"}
      </AppButton>
      {currentStep < steps.length - 1 ? (
        <AppButton variant="primary" onClick={handleNextStep} loading={importData.isImporting}>
          Continuer
        </AppButton>
      ) : (
        <AppButton variant="primary" onClick={handleSubmit} loading={importData.isLoading} disabled={!rowsCount}
          startIcon={<CloudUploadOutlined fontSize="small" />}>
          Importer {rowsCount} candidat{rowsCount > 1 ? 's' : ''}
        </AppButton>
      )}
    </>
  );

  return (
    <Modal open={!!modalState.open} onClose={handleClose} title="Importer des candidats" maxWidth={currentStep === 2 ? 'lg' : 'md'} actions={actions}>
      <ImportStepper steps={steps} current={currentStep} errorStep={errors.candidatsFile && currentStep === 1 ? 1 : undefined} />
      {importData.isImporting ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 5 }}>
          <CircularProgress size={28} />
          <Typography sx={{ fontSize: '0.88rem', color: '#64748b' }}>Lecture du fichier…</Typography>
        </Box>
      ) : getStepContent()}
    </Modal>
  );
};

export default withSnackbar(CandidatImport);
